import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';

// Models
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { DealerInfoModel, DealerInfoEntities } from 'client/data/models/dealer-info';
import { DealerInventoryModel } from 'client/data/models/dealer-inventory';
// Tracking
import { TrackingMap } from 'client/tracking/maps/dealer-details-page/index';
import { TrackingHandler } from 'client/tracking/handler';
import { EngagementHandler } from 'client/engagement-handlers/index';
import { DealerInternalReferrerEngagementHandler } from 'client/engagement-handlers/dealer-internal-referrer-engagement-handler/dealer-internal-referrer-engagement-handler';
// Utils
import { makeDDPCityStateName } from 'site-modules/shared/utils/nice-name';
import { encodeAndLowercaseDealerLogicalName } from 'client/site-modules/dealerships/utils/logical-name';
// Constants
import { DEALER_PROPS } from 'client/constants/dealer-reviews';
// Components
import { DealerInventoryCarousel } from 'site-modules/dealerships/components/dealer-inventory-carousel/dealer-inventory-carousel';
import { DealerReviews } from 'site-modules/dealerships/components/dealer-reviews/dealer-reviews';
import { DealerVideos } from 'site-modules/dealerships/components/dealer-videos/dealer-videos';
import { DealerAboutInfoUI } from 'site-modules/dealerships/components/dealer-about-info/dealer-about-info';
import { DealerDetails } from 'site-modules/dealerships/components/dealer-details/dealer-details';
import { DealerSeoLinks } from 'site-modules/dealerships/components/dealer-seo-links/dealer-seo-links';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';
import { IcoWidget } from 'client/site-modules/shared/components/dealer-ico-widget/ico-widget/ico-widget';
import { IcoReducedHeightStandard } from 'site-modules/shared/components/dealer-ico-widget/ico-reduced-height-standard/ico-reduced-height-standard';
import { DealerHeader } from 'site-modules/dealerships/components/dealer-header/dealer-header';
import { DealerInfoContent } from 'site-modules/dealerships/components/dealer-info-content/dealer-info-content';

import './dealer.scss';

EngagementHandler.register(DealerInternalReferrerEngagementHandler);

/**
 * Add Sticky for proper positioning on the landing page
 * Several empty containers were added as well (to demonstrate scrolling)
 */
function DealerDetailsPageUI({
  dealerInventory = {
    rooftopCounters: {},
    models: [],
  },
  dealerInfo = {
    showroomPhotos: [],
    name: '',
    videos: [],
  },
  params: propsParams,
}) {
  useEffect(() => {
    TrackingHandler.useMap(TrackingMap);
  }, []);
  const dealerIdRaw = dealerInfo?.rooftopId;

  if (!dealerIdRaw) {
    return null;
  }

  const dealerId = dealerIdRaw.toString();
  // Populate missing params when using dealer ID
  const params = {
    ...propsParams,
    dealerCity: makeDDPCityStateName(get(dealerInfo, 'address.city')),
    dealerState: makeDDPCityStateName(get(dealerInfo, 'address.stateName')),
    logicalName: dealerInfo.logicalName,
    dealerId,
  };

  return (
    <main id="main-content" tabIndex={-1} className="dealer-page">
      <DealerHeader dealerInfo={dealerInfo} />
      <DealerInfoContent dealerInfo={dealerInfo} />
      <DealerDetails
        dealerInfo={dealerInfo}
        hasShowroomPhotos={!!(dealerInfo && dealerInfo.showroomPhotos && !!dealerInfo.showroomPhotos.length)}
      />
      <div className="px-1 py-1 pt-lg-2 pb-lg-0">
        <IcoReducedHeightStandard dealerId={dealerId} IcoWidgetComponent={IcoWidget} />
      </div>
      <hr className="mb-0 mt-2 hidden-md-down" />
      <DealerReviews dealerInfo={dealerInfo} params={params} />
      {dealerInventory && dealerInventory.models && dealerInventory.models.length > 0 && (
        <MinHeightWrapper componentType="DEALER_INVENTORY_CAROUSEL" className="mb-3">
          <DealerInventoryCarousel makeModels={dealerInventory} />
        </MinHeightWrapper>
      )}
      <DealerVideos dealerInfo={dealerInfo} />
      <DealerAboutInfoUI dealerInfo={dealerInfo} />
      <DealerSeoLinks className="mb-3" />
    </main>
  );
}

DealerDetailsPageUI.propTypes = {
  params: DEALER_PROPS.isRequired,
  dealerInventory: PropTypes.shape({
    rooftopId: PropTypes.number,
    rooftopCounters: PropTypes.shape({
      total: PropTypes.number,
      new: PropTypes.number,
      cpo: PropTypes.number,
      used: PropTypes.number,
    }),
    models: PropTypes.arrayOf(PropTypes.object),
  }),
  dealerInfo: DealerInfoEntities.DealerInfo,
};

/**
 * @param {string} rootPath E.g., dealerInfo
 * @param {object} props Should contain target source value.
 *  E.g., props.params.logicalName = Abc
 * @returns {string} [rootPath].name/.id path version,
 *  based on available target props value. Empty if unavailable.
 *  E.g., 'dealerInfo.name.Abc'
 */
export function getDealerModelPath(rootPath, props) {
  const { logicalName } = props.params;
  return `${rootPath}.name.${encodeAndLowercaseDealerLogicalName(logicalName)}`;
}

export const stateToPropsConfig = {
  dealerInventory: bindToPath(props => getDealerModelPath('dealerInventory', props), DealerInventoryModel, null, false),
  dealerInfo: bindToPath(props => getDealerModelPath('dealerInfo', props), DealerInfoModel, null, false),
};

export const DealerDetailsPage = connectToModel(DealerDetailsPageUI, stateToPropsConfig);
